'use client';

import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIsMounted } from 'usehooks-ts';

import {
  getSessionStorage,
  setSessionStorage,
} from '@/shared/utils/typed-session-storage';

type HistoryAction = 'push' | 'pop' | 'replace' | null;

export default function NavigationHistory() {
  const [history,
    setHistory] = useState<string[]>([]);
  const actionRef = useRef<HistoryAction>(null);
  const isMounted = useIsMounted();

  // Load history on mount
  useEffect(() => {
    const saved = getSessionStorage('navigationHistory');

    if (Array.isArray(saved)) setHistory(saved);
  }, [isMounted]);

  // Save history when it changes
  useEffect(() => {
    setSessionStorage('navigationHistory', history);
  }, [history]);

  // Handle back/forward events
  useEffect(() => {
    const handlePopState = () => {
      actionRef.current = 'pop';
    };

    window.addEventListener('popstate', handlePopState);

    return () => { window.removeEventListener('popstate', handlePopState); };
  }, [isMounted]);

  // Track route changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const url = window.location.pathname + window.location.search;

      setHistory((previous) => {
        if (actionRef.current === 'pop') {
          return previous.slice(0, -1);
        }

        if (previous.at(-1) !== url) {
          return [...previous, url];
        }

        return previous;
      });

      actionRef.current = null;
    });

    observer.observe(document, { childList: true, subtree: true });

    return () => { observer.disconnect(); };
  }, [isMounted]);

  return null;
}
